import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import imgFinans      from "../assets/finansasset.png";
import imgGorev       from "../assets/gorevlerasset.png";
import imgTakvim      from "../assets/takvimasset.png";
import imgAlisveris   from "../assets/alisverisasset.png";
import imgSaglik      from "../assets/saglikasset.png";
import imgBelgeler    from "../assets/belgelerasset.png";
import imgCocuk       from "../assets/cocuktakibiasset.png";
import imgHarcama     from "../assets/harcamalarasset.png";
import imgHarita      from "../assets/aileharitasiasset.png";
import imgChat        from "../assets/ailesohbetiasset.png";
import { createPortal } from "react-dom";
import { useLanguage } from "../i18n/LanguageContext";
import { getAssistantSummary, readCachedAssistantSummary } from "../services/assistantService";
import { subscribeAppResume } from "../services/appResumeService";
import {
  areAndroidHealthFeaturesDisabled,
  isAndroidRestrictedModule,
} from "../utils/platformFeatures";
import aileasistaniImg from "../assets/aileasistaniasset.png";
import "../styles/pages/assistant.css";

/* ─── Routing map ─── */
const MODULE_ROUTE = {
  finance: "finans", tasks: "gorev", calendar: "takvim", shopping: "alisveris",
  health: "saglik", documents: "belgeler", children: "cocuk", map: "harita",
  chat: "chat", notifications: "ana", sharedExpenses: "harcama",
};

/* ─── Per-module visual config ─── */
const MODULE_META = {
  finance:        { img: imgFinans,    color: "#10b981" },
  tasks:          { img: imgGorev,     color: "#6366f1" },
  calendar:       { img: imgTakvim,    color: "#0ea5e9" },
  shopping:       { img: imgAlisveris, color: "#f59e0b" },
  health:         { img: imgSaglik,    color: "#ef4444" },
  documents:      { img: imgBelgeler,  color: "#8b5cf6" },
  children:       { img: imgCocuk,     color: "#ec4899" },
  sharedExpenses: { img: imgHarcama,   color: "#06b6d4" },
  map:            { img: imgHarita,    color: "#14b8a6" },
  chat:           { img: imgChat,      color: "#3b82f6" },
  notifications:  { img: null,         icon: "🔔", color: "#f97316" },
};

/* MODULE_META img render yardımcısı */
function ModuleImg({ meta, className, size }) {
  if (meta && meta.img) return <img src={meta.img} alt="" className={className} style={{ width: size, height: size, objectFit: "contain", display: "block" }} />;
  return <span>{meta && meta.icon ? meta.icon : "📊"}</span>;
}

const SUG_META = {
  danger:  { icon: "🚨", color: "#ef4444" },
  warning: { icon: "⚠️",  color: "#f59e0b" },
  success: { icon: "✅", color: "#10b981" },
  info:    { icon: "💡", color: "#6366f1" },
  cross:   { icon: "🔗", color: "#8b5cf6" },
};

const METRIC_LABELS = {
  tr: {
    total: "Toplam", open: "Açık", overdue: "Geciken", today: "Bugün",
    upcoming: "Yaklaşan", highPriority: "Yüksek", completionRate: "Tamamlanma",
    week: "Bu hafta", pending: "Bekleyen", oldPending: "Eski",
    income: "Gelir", expense: "Gider", net: "Net",
    billsDue7: "7 günde", overdueBills: "Geciken", activeSubscriptions: "Abonelik",
    subscriptionMonthly: "Aylık abo.", activeInstallments: "Taksit",
    installmentMonthly: "Taksit/ay", totalSavings: "Birikim",
    monthTotal: "Bu ay", count: "Kayıt", expired: "Süresi dolmuş",
    due30: "30 gün", records: "Takip", activeShares: "Paylaşan",
    recentMessages: "Mesaj", unread: "Okunmamış", notificationCount: "Bildirim",
    month: "Bu Ay", places: "Ortak Yer", geofences: "Sınır", safeZones: "Güvenli Alan",
    locationPoints: "Konum Sayısı", roomCount: "Sohbet", chatNotifications: "Sohbet", totalCount: "Toplam",
  },
  en: {
    total: "Total", open: "Open", overdue: "Overdue", today: "Today",
    upcoming: "Upcoming", highPriority: "High", completionRate: "Done",
    week: "This week", pending: "Pending", oldPending: "Old",
    income: "Income", expense: "Expense", net: "Net",
    billsDue7: "In 7 days", overdueBills: "Overdue", activeSubscriptions: "Subs",
    subscriptionMonthly: "Monthly subs", activeInstallments: "Instalments",
    installmentMonthly: "Inst./mo", totalSavings: "Savings",
    monthTotal: "This month", count: "Records", expired: "Expired",
    due30: "30 days", records: "Tracking", activeShares: "Sharing",
    recentMessages: "Messages", unread: "Unread", notificationCount: "Notifications",
    month: "This Month", places: "Places", geofences: "Geofences", safeZones: "Safe Zones",
    locationPoints: "Location Count", roomCount: "Chats", chatNotifications: "Chat", totalCount: "Total",
  },
};

const MODULE_METRICS = {
  finance:        ["net", "income", "expense", "totalSavings", "billsDue7", "activeSubscriptions"],
  tasks:          ["open", "overdue", "today", "highPriority"],
  calendar:       ["today", "week", "month"],
  shopping:       ["pending", "total"],
  health:         ["total", "today", "upcoming"],
  documents:      ["total", "expired", "due30"],
  children:       ["total", "records"],
  sharedExpenses: ["monthTotal", "count"],
  map:            ["activeShares", "places", "geofences", "safeZones", "locationPoints"],
  chat:           ["roomCount", "unread"],
  notifications:  ["notificationCount", "unread", "chatNotifications"],
};

/* ─── Helpers ─── */
function fmtDate(value, lang) {
  if (!value) return "";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString(lang === "en" ? "en-US" : "tr-TR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch (_) { return ""; }
}

function formatMetricValue(value, key, lang) {
  if (typeof value !== "number") return String(value ?? "");
  if (["income","expense","net","monthTotal","subscriptionMonthly","installmentMonthly","totalSavings"].includes(key)) {
    return value.toLocaleString(lang === "en" ? "en-US" : "tr-TR", { maximumFractionDigits: 0 });
  }
  if (key === "completionRate") return `%${value}`;
  return String(value);
}

/* Fixed time greeting with night hours */
function getTimeGreeting(lang) {
  const h = new Date().getHours();
  if (lang === "en") {
    if (h >= 0  && h < 6)  return "Good night";
    if (h >= 6  && h < 12) return "Good morning";
    if (h >= 12 && h < 17) return "Good afternoon";
    return "Good evening";
  }
  if (h >= 0  && h < 6)  return "İyi geceler";
  if (h >= 6  && h < 12) return "Günaydın";
  if (h >= 12 && h < 17) return "İyi öğleden sonralar";
  return "İyi akşamlar";
}

function getModuleStatus(id, data) {
  if (!data || Object.keys(data).length === 0) return "neutral";
  if (id === "tasks")         return data.overdue > 0 ? "danger" : (data.today > 0 || data.open > 3) ? "warning" : "good";
  if (id === "finance")       return (data.overdueBills > 0 || data.net < 0) ? "danger" : data.billsDue7 > 0 ? "warning" : "good";
  if (id === "documents")     return data.expired > 0 ? "danger" : data.due30 > 0 ? "warning" : "good";
  if (id === "health")        return data.today > 0 ? "warning" : "good";
  if (id === "shopping")      return (data.pending || 0) > 5 ? "warning" : (data.pending || 0) > 0 ? "good" : "good";
  if (id === "children")      return data.stale ? "warning" : "good";
  if (id === "calendar")      return data.today > 0 ? "warning" : "good";
  if (id === "chat")          return (data.unread || 0) > 0 ? "warning" : "good";
  if (id === "map")           return (data.activeShares || 0) > 0 ? "good" : "neutral";
  if (id === "sharedExpenses") return (data.monthTotal || 0) > 0 ? "good" : "neutral";
  if (id === "notifications") {
    const c = Number(data.notificationCount ?? data.unread ?? 0);
    return c > 5 ? "danger" : c > 0 ? "warning" : "good";
  }
  return "good";
}

/* ─── ScoreRing + explanation popup ─── */
function ScoreRing({ value, breakdown, lang, t }) {
  const [open, setOpen] = useState(false);
  const size = 82;
  const r = (size - 14) / 2;
  const circ = 2 * Math.PI * r;
  const v = Math.min(100, Math.max(0, value ?? 0));
  const offset = circ - (v / 100) * circ;
  const color = v >= 75 ? "#22c55e" : v >= 50 ? "#f59e0b" : "#ef4444";
  const items = Array.isArray(breakdown?.items) ? breakdown.items : [];
  const plusTotal = items.filter((x) => x.type === "plus").reduce((a, x) => a + Number(x.points || 0), 0);
  const minusTotal = Math.abs(items.filter((x) => x.type === "minus").reduce((a, x) => a + Number(x.points || 0), 0));

  const modal = open && typeof document !== "undefined" ? createPortal(
    <div className="scoreModalLayer" role="presentation">
      <button
        type="button"
        className="scorePopupBackdrop"
        aria-label={lang === "en" ? "Close" : "Kapat"}
        onClick={() => setOpen(false)}
      />
      <div className="scorePopup" role="dialog" aria-modal="true" aria-label={t("assistant.scoreExplain")}>
        <div className="scorePopupHead">
          <strong>{t("assistant.scoreExplain")}</strong>
          <button type="button" aria-label={lang === "en" ? "Close" : "Kapat"} onClick={() => setOpen(false)}>×</button>
        </div>
        <div className="scorePopupTotals">
          <span className="scorePlus">+{plusTotal}</span>
          <span className="scoreMinus">-{minusTotal}</span>
          <span>{lang === "en" ? `Result: ${v}` : `Sonuç: ${v}`}</span>
        </div>
        <div className="scorePopupList">
          {items.map((item, i) => (
            <div key={i} className={`scorePopupItem item-${item.type || "base"}`}>
              <span>{item.type === "plus" ? "+" : item.type === "minus" ? "−" : "="}{Math.abs(Number(item.points || 0))}</span>
              <p>{item.label}</p>
            </div>
          ))}
        </div>
        {breakdown?.note && <p className="scorePopupNote">{breakdown.note}</p>}
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <>
      <div className="scoreRingArea">
        <button
          type="button"
          className="scoreRingWrap"
          onClick={() => setOpen((x) => !x)}
          aria-label={t("assistant.scoreExplain")}
        >
          <svg viewBox={`0 0 ${size} ${size}`} className="scoreRingSvg" aria-hidden="true">
            <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
              <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(148,163,184,0.14)" strokeWidth={10} />
              <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={10}
                strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
                style={{ transition: "stroke-dashoffset 1.2s cubic-bezier(.4,0,.2,1), stroke 0.5s ease" }} />
            </g>
          </svg>
          <div className="scoreRingCenter">
            <strong style={{ color }}>{value ?? "--"}</strong>
            <span>{lang === "en" ? "score" : "puan"}</span>
          </div>
        </button>
      </div>
      {modal}
    </>
  );
}

/* ─── AiOrb — now uses the app's image asset ─── */
function AiOrb({ loading }) {
  return (
    <div className={`aiOrbWrap${loading ? " orbLoading" : ""}`}>
      <div className="aiOrbRing" aria-hidden="true" />
      <div className="aiOrb">
        <img src={aileasistaniImg} alt="Aile Asistanı" className="aiOrbImg" />
      </div>
    </div>
  );
}

/* ─── HeroPanel ─── */
function HeroPanel({ summary, loading, onRefresh, lang, t, userName }) {
  const greeting = getTimeGreeting(lang);
  const personalGreeting = [greeting, userName].filter(Boolean).join(" ");
  const scoreValue = Number.isFinite(Number(summary?.score)) ? Number(summary.score) : null;
  const hasData = !!summary;

  return (
    <section className="aiHeroPanel">
      <div className="aiHeroLeft">
        <AiOrb loading={loading && !hasData} />
        <div className="aiHeroText">
          <div className="aiGreetingRow">
            <span className="aiGreeting" title={personalGreeting}>{personalGreeting}</span>
          </div>
          <h1 className="aiHeadline">
            {loading && !hasData ? t("assistant.loading") : summary?.headline || t("assistant.empty.title")}
          </h1>
          {summary?.generatedAt && (
            <span className="aiTimestamp">
              {t("assistant.updatedNow")} · {fmtDate(summary.generatedAt, lang)}
            </span>
          )}
        </div>
      </div>
      <div className="aiHeroRight">
        {scoreValue !== null ? <ScoreRing value={scoreValue} breakdown={summary?.scoreBreakdown} lang={lang} t={t} /> : <div className="scoreRingPlaceholder">--</div>}
        <button
          className="aiRefreshBtn"
          onClick={onRefresh}
          disabled={loading}
          title={t("assistant.refresh")}
        >
          <span className={loading ? "refreshSpin" : ""}>↻</span>
        </button>
      </div>
    </section>
  );
}

/* ─── PriorityCarousel ─── */
function PriorityCarousel({ items, onOpen, t }) {
  const [idx, setIdx] = useState(0);

  if (!items || items.length === 0) {
    return (
      <section className="prioritySection">
        <div className="sectionLabel"><span>⚡</span><h2>{t("assistant.priority")}</h2></div>
        <div className="priorityEmptyCard">
          <strong>{t("assistant.empty.title")}</strong>
          <p>{t("assistant.empty.desc")}</p>
        </div>
      </section>
    );
  }

  return (
    <section className="prioritySection">
      <div className="sectionLabel">
        <span>⚡</span>
        <h2>{t("assistant.priority")}</h2>
        <span className="priorityCount">{idx + 1}/{items.length}</span>
      </div>
      <div
        className="priorityList"
        data-no-swipe-back="true"
        onScroll={(e) => {
          const w = e.currentTarget.clientWidth || 1;
          setIdx(Math.round(e.currentTarget.scrollLeft / w));
        }}
      >
        {items.map((item, i) => (
          <button
            key={i}
            className={`priorityCard pType-${item.type || "info"}`}
            onClick={() => onOpen(MODULE_ROUTE[item.module] || "ana")}
          >
            <div className="priorityCardInner">
              <div className="priorityBadge">{i + 1}</div>
              <div className="priorityBody">
                <span className="priorityTypeLabel">{item.type === "danger" ? "🚨" : item.type === "warning" ? "⚠️" : "ℹ️"}</span>
                <p>{item.text}</p>
              </div>
              <span className="priorityArrow">›</span>
            </div>
          </button>
        ))}
      </div>
      {items.length > 1 && (
        <div className="priorityDots">
          {items.map((_, i) => <i key={i} className={i === idx ? "active" : ""} />)}
        </div>
      )}
    </section>
  );
}

/* ─── SuggestionBubble ─── */
function SuggestionBubble({ suggestion, isCross }) {
  const type = isCross ? "cross" : (suggestion.type || "info");
  const meta = SUG_META[type] || SUG_META.info;
  return (
    <div className={`sugBubble sugType-${type}`} style={{ "--sug-color": meta.color }}>
      <span className="sugIcon">{meta.icon}</span>
      <p>{suggestion.text}</p>
    </div>
  );
}

/* ─── SuggestionsPanel ─── */
function SuggestionsPanel({ allSuggestions, crossSuggestions, moduleCards, lang, t }) {
  const [filter, setFilter] = useState("all");

  const availableModules = useMemo(() => {
    const mods = new Set(allSuggestions.map((s) => s.module).filter(Boolean));
    return moduleCards.filter(([id]) => mods.has(id)).slice(0, 7);
  }, [allSuggestions, moduleCards]);

  const filtered = useMemo(() => {
    if (filter === "all") {
      const cross = crossSuggestions.map((s) => ({ ...s, _isCross: true }));
      return [...cross, ...allSuggestions];
    }
    return allSuggestions.filter((s) => s.module === filter);
  }, [filter, allSuggestions, crossSuggestions]);

  if (allSuggestions.length === 0 && crossSuggestions.length === 0) return null;

  return (
    <section className="suggestionsPanel">
      {/* "AI" is now part of the title, not a separate pill */}
      <div className="sectionLabel premium">
        <span>✨</span>
        <h2>{t("assistant.smartAdvice")}</h2>
        <span className="sugCount">{filtered.length}</span>
      </div>

      <div className="filterChipBar" data-no-swipe-back="true">
        <button className={`filterChip${filter === "all" ? " active" : ""}`} onClick={() => setFilter("all")}>
          {lang === "en" ? "All" : "Tümü"}
        </button>
        {availableModules.map(([id, , img]) => (
          <button key={id} className={`filterChip${filter === id ? " active" : ""}`}
            onClick={() => setFilter(id)}
            style={{ "--chip-color": MODULE_META[id]?.color || "#6366f1" }}>
            {img && img !== "🔔"
              ? <img src={img} alt="" className="chipModuleImg" />
              : <span>{img || "🔔"}</span>}
          </button>
        ))}
      </div>

      <div className="suggestionList" data-no-swipe-back="true">
        {filtered.length === 0 ? (
          <div className="sugEmpty">{t("assistant.empty.desc")}</div>
        ) : filtered.map((s, i) => (
          <SuggestionBubble key={`${filter}-${i}`} suggestion={s} isCross={!!s._isCross} />
        ))}
      </div>
    </section>
  );
}

/* ─── ModuleTabsPanel — tab bar + swipeable detail panels ─── */
function ModuleTabsPanel({ modules, moduleCards, suggestionsByModule, onOpen, t, lang }) {
  const [activeTab, setActiveTab] = useState(moduleCards[0]?.[0]);
  const tabBarRef   = useRef(null);
  const panelListRef = useRef(null);
  const isScrollingByTab = useRef(false);

  /* Scroll tab bar so active tab is visible */
  const scrollTabIntoView = useCallback((id) => {
    const bar = tabBarRef.current;
    if (!bar) return;
    const btn = bar.querySelector(`[data-tabid="${id}"]`);
    if (btn) btn.scrollIntoView({ inline: "center", behavior: "smooth", block: "nearest" });
  }, []);

  /* Click on tab → scroll panel list */
  const handleTabClick = useCallback((id) => {
    const idx = moduleCards.findIndex(([mid]) => mid === id);
    setActiveTab(id);
    scrollTabIntoView(id);
    if (idx >= 0 && panelListRef.current) {
      isScrollingByTab.current = true;
      panelListRef.current.scrollTo({ left: idx * panelListRef.current.clientWidth, behavior: "smooth" });
      setTimeout(() => { isScrollingByTab.current = false; }, 400);
    }
  }, [moduleCards, scrollTabIntoView]);

  /* Swipe panel → update tab bar */
  const handlePanelScroll = useCallback((e) => {
    if (isScrollingByTab.current) return;
    const el = e.currentTarget;
    const idx = Math.round(el.scrollLeft / el.clientWidth);
    const newId = moduleCards[idx]?.[0];
    if (newId && newId !== activeTab) {
      setActiveTab(newId);
      const bar = tabBarRef.current;
      if (bar) {
        const btn = bar.querySelector(`[data-tabid="${newId}"]`);
        if (btn) btn.scrollIntoView({ inline: "center", behavior: "smooth", block: "nearest" });
      }
    }
  }, [moduleCards, activeTab]);

  return (
    <section className="moduleTabsPanel">
      <div className="sectionLabel">
        <span>📊</span>
        <h2>{lang === "en" ? "Modules" : "Modüller"}</h2>
      </div>

      {/* Horizontal tab bar */}
      <div className="moduleTabBar" ref={tabBarRef} data-no-swipe-back="true">
        {moduleCards.map(([id, title, icon]) => {
          const status = getModuleStatus(id, modules[id] || {});
          return (
            <button
              key={id}
              data-tabid={id}
              className={`moduleTab${id === activeTab ? " active" : ""}`}
              style={{ "--tab-color": MODULE_META[id]?.color || "#6366f1" }}
              onClick={() => handleTabClick(id)}
            >
              <span className={`modStatusDot s-${status}`} />
              <span className="modTabIcon">
                {icon && icon !== "🔔"
                  ? <img src={icon} alt="" className="modTabIconImg" />
                  : <span>{icon || "🔔"}</span>}
              </span>
              <span className="modTabLabel">{title}</span>
            </button>
          );
        })}
      </div>

      {/* Swipeable panels — one panel per module */}
      <div
        className="moduleDetailList"
        ref={panelListRef}
        onScroll={handlePanelScroll}
        data-no-swipe-back="true"
      >
        {moduleCards.map(([id, title, icon]) => {
          const data        = modules[id] || {};
          const meta        = MODULE_META[id] || { icon: "📊", color: "#6366f1" };
          const modSugs     = suggestionsByModule[id] || [];
          const EXCLUDE_KEYS = ["shortText","disclaimer","stale","lastRecordAt","latestUpdateAt","lastMessageAt"];
          const metricKeys  = (MODULE_METRICS[id] || []).filter((k) => {
            const v = data[k];
            if (EXCLUDE_KEYS.includes(k)) return false;
            // 0 dahil tüm sayısal ve string değerleri göster
            return typeof v === "number" || (typeof v === "string" && v !== "");
          }).slice(0, 6);

          return (
            <div key={id} className="moduleDetail" style={{ "--mod-color": meta.color }}>
              <div className="moduleDetailTop">
                <div className="modDetailIcon">
                  <ModuleImg meta={meta} className="modDetailIconImg" size={28} />
                </div>
                <div className="modDetailMeta">
                  <h3>{title}</h3>
                  <p>{data.shortText || ""}</p>
                </div>
                <button className="modGoBtn" onClick={() => onOpen(MODULE_ROUTE[id] || "ana")}>
                  {t("assistant.viewDetails")} <span>›</span>
                </button>
              </div>

              {metricKeys.length > 0 && (
                <div className={`modMetricGrid cols-${Math.min(metricKeys.length, 3)}`}>
                  {metricKeys.map((k) => (
                    <div className="modMetric" key={k}>
                      <strong>{formatMetricValue(data[k], k, lang)}</strong>
                      <span>{METRIC_LABELS[lang]?.[k] || k}</span>
                    </div>
                  ))}
                </div>
              )}

              {modSugs.length > 0 && (
                <div className="modSuggestionList">
                  {modSugs.slice(0, 3).map((s, i) => (
                    <div key={i} className={`modSugItem modSugType-${s.type || "info"}`}>
                      <span>{SUG_META[s.type || "info"]?.icon}</span>
                      <p>{s.text}</p>
                    </div>
                  ))}
                </div>
              )}

              {id === "health" && data.disclaimer && (
                <p className="moduleDisclaimer">{data.disclaimer}</p>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* ─── Main Page ─── */
function AssistantPage({
  familyId, setAktifSayfa, navigateTo, navigateFromAssistant, refreshKey,
  user, userName: providedUserName,
  /* Canlı frontend verisi — backend summary'nin önüne geçer */
  aktifKonumPaylasan, toplamOrtakYer = 0, toplamSinir = 0, toplamGuvenliAlan = 0, toplamKonumGecmisi = 0,
  toplamGorev, yaklasanlar, toplamEtkinlikBuAy = 0,
  toplamUrun, bekleyenUrun,
  netBakiye, toplamOrtakHarcama,
  toplamSaglikKaydi,
  toplamBelge,
  cocukKartSayisi, cocukTakipKaydi,
  okunmamisChat, toplamSohbet = 0,
  toplamOkunmamisBildirim, toplamBildirim = 0, toplamOkunmamisSistemBildirim = 0, toplamOkunmamisSohbetBildirim = 0,
}) {
  const { t, lang } = useLanguage();
  const userName = String(providedUserName || user?.ad || user?.firstName || user?.displayName || user?.name || "").trim().split(/\s+/)[0];
  const cachedInitial = useMemo(() => readCachedAssistantSummary(familyId, lang), [familyId, lang]);
  const [summary, setSummary] = useState(cachedInitial);
  const [loading, setLoading] = useState(!cachedInitial);
  const [error, setError] = useState("");
  const lastLoadRef = useRef(0);

  const load = useCallback(async ({ silent = false, force = false } = {}) => {
    if (!familyId) return;
    const now = Date.now();
    if (silent && now - lastLoadRef.current < 900) return;
    lastLoadRef.current = now;
    if (!silent && !summary) setLoading(true);
    setError("");
    try {
      const data = await getAssistantSummary(familyId, lang, { force });
      setSummary(data);
    } catch (_) {
      setError(t("assistant.error"));
    } finally {
      if (!silent) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [familyId, lang]);

  useEffect(() => {
    const cached = readCachedAssistantSummary(familyId, lang);
    if (cached) { setSummary(cached); setLoading(false); load({ silent: true }); }
    else load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [familyId, lang]);

  useEffect(() => { if (familyId) load({ silent: true }); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [refreshKey]);

  useEffect(() => {
    return subscribeAppResume(() => load({ silent: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [familyId, lang]);

  const openPage = useCallback((page) => {
    if (typeof navigateFromAssistant === "function") navigateFromAssistant(page);
    else if (typeof navigateTo === "function") navigateTo(page);
    else if (typeof setAktifSayfa === "function") setAktifSayfa(page);
  }, [navigateFromAssistant, navigateTo, setAktifSayfa]);

  const allSuggestions   = useMemo(() => (summary?.suggestions || []), [summary]);
  const crossSuggestions = useMemo(() => (summary?.crossSuggestions || []), [summary]);

  const moduleCards = useMemo(() => [
    ["finance",        t("nav.finance"),             imgFinans],
    ["tasks",          t("nav.tasks"),               imgGorev],
    ["calendar",       t("nav.calendar"),            imgTakvim],
    ["shopping",       t("nav.shopping"),            imgAlisveris],
    ["health",         t("nav.health"),              imgSaglik],
    ["documents",      t("nav.documents"),           imgBelgeler],
    ["children",       t("nav.children"),            imgCocuk],
    ["sharedExpenses", t("nav.sharedExpenses"),      imgHarcama],
    ["map",            t("nav.map"),                 imgHarita],
    ["chat",           t("nav.chat"),                imgChat],
    ["notifications",  t("assistant.notifications"), "🔔"],
  ].filter(([id]) => !isAndroidRestrictedModule(id)), [t]);

  // Frontend'deki canlı değerleri hesapla
  const liveData = useMemo(() => ({
    tasks:          { open: Number(toplamGorev || 0) },
    shopping:       { pending: Number(bekleyenUrun || 0), total: Number(toplamUrun || 0) },
    calendar:       { today: Array.isArray(yaklasanlar) ? yaklasanlar.length : 0,
                      week:  Array.isArray(yaklasanlar) ? yaklasanlar.length : 0,
                      month: Number(toplamEtkinlikBuAy || 0) },
    finance:        { net: Number(netBakiye || 0) },
    sharedExpenses: { monthTotal: Number(toplamOrtakHarcama || 0) },
    health:         { total: Number(toplamSaglikKaydi || 0) },
    documents:      { total: Number(toplamBelge || 0) },
    children:       { total: Number(cocukKartSayisi || 0), records: Number(cocukTakipKaydi || 0) },
    map:            {
                      activeShares: Number(aktifKonumPaylasan || 0),
                      places: Number(toplamOrtakYer || 0),
                      geofences: Number(toplamSinir || 0),
                      safeZones: Number(toplamGuvenliAlan || 0),
                      locationPoints: Number(toplamKonumGecmisi || 0),
                    },
    chat:           { unread: Number(okunmamisChat || 0), roomCount: Number(toplamSohbet || 0) },
    notifications:  { notificationCount: Number(toplamBildirim || toplamOkunmamisBildirim || 0),
                      unread: Number(toplamOkunmamisSistemBildirim || toplamOkunmamisBildirim || 0),
                      chatNotifications: Number(toplamOkunmamisSohbetBildirim || 0) },
  }), [toplamGorev, bekleyenUrun, toplamUrun, yaklasanlar, toplamEtkinlikBuAy, netBakiye,
       toplamOrtakHarcama, toplamSaglikKaydi, toplamBelge,
       cocukKartSayisi, cocukTakipKaydi, aktifKonumPaylasan, toplamOrtakYer, toplamSinir, toplamGuvenliAlan, toplamKonumGecmisi,
       okunmamisChat, toplamSohbet, toplamOkunmamisBildirim, toplamBildirim, toplamOkunmamisSistemBildirim, toplamOkunmamisSohbetBildirim]);

  const modules = useMemo(() => {
    const base = summary?.modules || {};
    const live = liveData;
    const t2 = (tr, en) => lang === "en" ? en : tr;

    // Backend modülünü frontend değerleriyle merge et
    // Frontend değerleri her zaman öncelikli
    const merge = (id, extra = {}) => ({
      ...(base[id] || {}),
      ...live[id],
      ...extra,
    });

    const gorev     = live.tasks.open;
    const bekleyen  = live.shopping.pending;
    const topUrun   = live.shopping.total;
    const etkinlik  = live.calendar.today;
    const buAyEtkinlik = live.calendar.month;
    const net       = live.finance.net;
    const harcama   = live.sharedExpenses.monthTotal;
    const saglik    = live.health.total;
    const belge     = live.documents.total;
    const cocukSay  = live.children.total;
    const cocukKay  = live.children.records;
    const konum     = live.map.activeShares;
    const ortakYer  = live.map.places;
    const sinir     = live.map.geofences;
    const guvenli   = live.map.safeZones;
    const konumGecmisi = live.map.locationPoints;
    const chat      = live.chat.unread;
    const sohbetSay = live.chat.roomCount;
    const bildirim  = live.notifications.notificationCount;
    const okunmamisBildirim = live.notifications.unread;
    const sohbetBildirim = live.notifications.chatNotifications;

    return {
      tasks:          { ...merge("tasks"),          shortText: t2(`${gorev} aktif görev var.`,      `${gorev} active task${gorev!==1?"s":""}.`) },
      shopping:       { ...merge("shopping"),       shortText: t2(`Alışverişte ${bekleyen} ürün bekliyor.`, `${bekleyen} item${bekleyen!==1?"s":""} waiting.`) },
      calendar:       { ...merge("calendar"),       shortText: t2(`${etkinlik} yaklaşan etkinlik, bu ay ${buAyEtkinlik} etkinlik var.`,     `${etkinlik} upcoming event${etkinlik!==1?"s":""}, ${buAyEtkinlik} this month.`) },
      finance:        { ...merge("finance"),        shortText: base.finance?.shortText || t2("Finans özeti hazır.", "Finance summary is available.") },
      sharedExpenses: { ...merge("sharedExpenses"), shortText: t2(`Bu ayki ortak harcama: ${harcama.toLocaleString("tr-TR")} ₺.`, `Shared expenses: ${harcama.toLocaleString("en-US")}.`) },
      health:         { ...merge("health"),         shortText: t2(`${saglik} sağlık kaydı takip ediliyor.`, `${saglik} health record${saglik!==1?"s":""} tracked.`) },
      documents:      { ...merge("documents"),      shortText: base.documents?.shortText || t2("Belge özeti hazır.", "Documents summary is ready.") },
      children:       { ...merge("children"),       shortText: t2(`${cocukSay} çocuk, ${cocukKay} takip kaydı.`, `${cocukSay} child${cocukSay!==1?"ren":""}, ${cocukKay} record${cocukKay!==1?"s":""}.`) },
      map:            { ...merge("map"),            shortText: t2(`${konum} kişi konum paylaşıyor, ${ortakYer} ortak yer, ${sinir} sınır, ${guvenli} güvenli alan, ${konumGecmisi} konum sayısı.`, `${konum} member${konum!==1?"s":""} sharing location, ${ortakYer} place${ortakYer!==1?"s":""}, ${sinir} geofence${sinir!==1?"s":""}, ${guvenli} safe zone${guvenli!==1?"s":""}, ${konumGecmisi} location count.`) },
      chat:           { ...merge("chat"),           shortText: t2(`${sohbetSay} sohbet, ${chat} okunmamış mesaj.`, `${sohbetSay} chat${sohbetSay!==1?"s":""}, ${chat} unread message${chat!==1?"s":""}.`) },
      notifications:  { ...merge("notifications"),  shortText: t2(`${bildirim} bildirim, ${okunmamisBildirim} okunmamış, ${sohbetBildirim} sohbet bildirimi.`, `${bildirim} notification${bildirim!==1?"s":""}, ${okunmamisBildirim} unread, ${sohbetBildirim} chat notification${sohbetBildirim!==1?"s":""}.`) },
    };
  }, [summary, lang, liveData]);
  const priorityItems = (summary?.priorityItems || [])
    .filter((item) => !isAndroidRestrictedModule(item?.module));

  // Frontend verisinden anlık yerel öncelikli konular — backend gelmeden önce gösterilir
  const localPriorityItems = useMemo(() => {
    const t2 = (tr, en) => lang === "en" ? en : tr;
    const items = [];
    const gorev    = Number(toplamGorev || 0);
    const bekleyen = Number(bekleyenUrun || 0);
    const etkinlik = Array.isArray(yaklasanlar) ? yaklasanlar.length : 0;
    const chat     = Number(okunmamisChat || 0);
    const bildirim = Number(toplamOkunmamisBildirim || 0);

    if (gorev > 0)    items.push({ type: "warning", module: "tasks",    text: t2(`${gorev} tamamlanmamış görev var.`, `${gorev} open task${gorev!==1?"s":""}.`) });
    if (bekleyen > 0) items.push({ type: "info",    module: "shopping", text: t2(`Alışveriş listesinde ${bekleyen} ürün bekliyor.`, `${bekleyen} shopping item${bekleyen!==1?"s":""} pending.`) });
    if (etkinlik > 0) items.push({ type: "info",    module: "calendar", text: t2(`${etkinlik} yaklaşan takvim etkinliği var.`, `${etkinlik} upcoming calendar event${etkinlik!==1?"s":""}.`) });
    if (chat > 0)     items.push({ type: "info",    module: "chat",     text: t2(`${chat} okunmamış sohbet mesajı.`, `${chat} unread message${chat!==1?"s":""}.`) });
    if (bildirim > 0) items.push({ type: "info",    module: "notifications", text: t2(`${bildirim} okunmamış bildirim.`, `${bildirim} unread notification${bildirim!==1?"s":""}.`) });
    return items;
  }, [lang, toplamGorev, bekleyenUrun, yaklasanlar, okunmamisChat, toplamOkunmamisBildirim]);

  // Gösterilecek öncelikli konular: backend gelmişse backend, yoksa yerel
  const displayedPriorityItems = priorityItems.length > 0 ? priorityItems : localPriorityItems;

  // Frontend verisinden anlık yerel tavsiyeler — backend suggestions boşsa kullanılır
  const localSuggestions = useMemo(() => {
    const t2 = (tr, en) => lang === "en" ? en : tr;
    const items = [];
    const gorev    = Number(toplamGorev || 0);
    const bekleyen = Number(bekleyenUrun || 0);
    const saglik   = Number(toplamSaglikKaydi || 0);
    const belge    = Number(toplamBelge || 0);
    const cocuk    = Number(cocukKartSayisi || 0);
    const net      = Number(netBakiye || 0);

    if (gorev > 3)     items.push({ type: "warning", module: "tasks",     text: t2(`${gorev} açık görevin var, bugün birini tamamlaya bilirsin.`, `You have ${gorev} open tasks — consider completing one today.`) });
    if (bekleyen > 0)  items.push({ type: "info",    module: "shopping",  text: t2(`Alışveriş listesinde ${bekleyen} bekleyen ürün var.`, `${bekleyen} item${bekleyen!==1?"s":""} still on your shopping list.`) });
    if (!areAndroidHealthFeaturesDisabled() && saglik === 0) items.push({ type: "info", module: "health", text: t2("Henüz sağlık kaydı yok. Bir aile üyesinin sağlık bilgisini ekleyebilirsin.", "No health records yet. Try adding one for a family member.") });
    if (belge === 0)   items.push({ type: "info",    module: "documents", text: t2("Henüz belge yüklenmemiş. Önemli aile belgelerini buraya ekleyebilirsin.", "No documents uploaded yet. Add important family documents here.") });
    if (!areAndroidHealthFeaturesDisabled() && cocuk > 0) items.push({ type: "info", module: "children", text: t2(`${cocuk} çocuk profili takip ediliyor.`, `${cocuk} child profile${cocuk!==1?"s":""} being tracked.`) });
    if (net < 0)       items.push({ type: "danger",  module: "finance",   text: t2("Bu ay giderlerin gelirden fazla. Finans sayfasını kontrol et.", "Expenses exceed income this month. Check your finance page.") });
    if (items.length === 0) items.push({ type: "info", module: "general", text: t2("Aile verilerini güncel tutmak için kayıt eklemeye devam et.", "Keep adding family records to get better insights.") });
    return items;
  }, [lang, toplamGorev, bekleyenUrun, toplamSaglikKaydi, toplamBelge, cocukKartSayisi, netBakiye]);

  const displayedSuggestions = (allSuggestions.length > 0 ? allSuggestions : localSuggestions)
    .filter((item) => !isAndroidRestrictedModule(item?.module));

  const suggestionsByModule = useMemo(() => {
    const map = {};
    displayedSuggestions.forEach((s) => {
      const key = s.module || "general";
      map[key] = map[key] || [];
      map[key].push(s);
    });
    return map;
  }, [displayedSuggestions]);

  return (
    <main className="assistantPage">
      <HeroPanel
        summary={summary}
        loading={loading}
        onRefresh={() => load({ force: true })}
        lang={lang}
        t={t}
        userName={userName}
      />

      {error && <div className="aiErrorBanner">{error}</div>}

      <PriorityCarousel items={displayedPriorityItems} onOpen={openPage} t={t} />

      <SuggestionsPanel
        allSuggestions={displayedSuggestions}
        crossSuggestions={crossSuggestions}
        moduleCards={moduleCards}
        lang={lang}
        t={t}
      />

      <ModuleTabsPanel
        modules={modules}
        moduleCards={moduleCards}
        suggestionsByModule={suggestionsByModule}
        onOpen={openPage}
        t={t}
        lang={lang}
      />
    </main>
  );
}

export default AssistantPage;
